import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import { findByIntegralPresencaCodigo, save as savePA } from "./api-integral-presenca-aluno";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import Loading from "../../UI/Loading";

const porChamada = (a, b) =>
  (a.alunoTurma?.numeroChamada ?? 0) - (b.alunoTurma?.numeroChamada ?? 0) ||
  (a.alunoTurma?.aluno?.nome ?? "").localeCompare(b.alunoTurma?.aluno?.nome ?? "");

/** Chamada dos alunos em um dia do integral. */
const IntegralPresencaAlunoTable = ({ integralPresenca }) => {
  const [listaPA, setListaPA] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [salvando, setSalvando] = useState({});

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    findByIntegralPresencaCodigo(integralPresenca.codigo)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message || "Não foi possível carregar a chamada.");
        } else if (Array.isArray(data)) {
          setListaPA([...data].sort(porChamada));
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [integralPresenca.codigo]);

  const handlePresenteChange = (pa) => {
    setSalvando((prevState) => ({ ...prevState, [pa.codigo]: true }));
    savePA({ ...pa, presente: !pa.presente })
      .then((data) => {
        if (!data || data.error) {
          errorMessage(data?.message || `Não foi possível salvar a presença de ${pa.alunoTurma?.aluno?.nome}.`);
          return;
        }
        setListaPA((prevState) => prevState.map((ps) => (ps.codigo === pa.codigo ? data : ps)));
      })
      .finally(() => setSalvando((prevState) => ({ ...prevState, [pa.codigo]: false })));
  };

  if (isLoading) {
    return (
      <Box py={6}>
        <Loading description="Carregando chamada..." />
      </Box>
    );
  }

  if (listaPA.length === 0) {
    return (
      <Box
        sx={{
          py: 6,
          textAlign: "center",
          color: "text.secondary",
          border: 1,
          borderStyle: "dashed",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <PeopleOutlineIcon fontSize="large" />
        <Typography variant="body2" mt={1}>
          Nenhum aluno na chamada deste dia.
        </Typography>
      </Box>
    );
  }

  const presentes = listaPA.filter((pa) => pa.presente).length;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1}>
        <Chip size="small" color="success" variant="outlined" label={`${presentes} presentes`} />
        <Chip size="small" color="error" variant="outlined" label={`${listaPA.length - presentes} faltas`} />
        <Chip size="small" variant="outlined" label={`${listaPA.length} alunos`} />
      </Stack>
      <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
        <Table size="small" aria-label="Chamada do integral">
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover" } }}>
              <TableCell sx={{ width: 72 }} align="center">
                Nº
              </TableCell>
              <TableCell>Aluno</TableCell>
              <TableCell align="right" sx={{ width: 160 }}>
                Presença
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {listaPA.map((pa) => (
              <TableRow key={pa.codigo} hover>
                <TableCell align="center">{pa.alunoTurma?.numeroChamada}</TableCell>
                <TableCell>
                  <Typography variant="body2">{pa.alunoTurma?.aluno?.nome}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Cód. {pa.alunoTurma?.aluno?.codAlunoEscola}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
                    <Typography variant="body2" color={pa.presente ? "success.main" : "error.main"} fontWeight={500}>
                      {pa.presente ? "Presente" : "Falta"}
                    </Typography>
                    <Switch
                      size="small"
                      color="success"
                      checked={Boolean(pa.presente)}
                      disabled={Boolean(salvando[pa.codigo])}
                      onChange={() => handlePresenteChange(pa)}
                      slotProps={{ input: { "aria-label": `Presença de ${pa.alunoTurma?.aluno?.nome}` } }}
                    />
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default IntegralPresencaAlunoTable;
